import React, { useState, useEffect } from 'react';
import { Card, List, Button, Tag, Modal, Descriptions, Steps, message, Tabs, Empty, Rate, Input } from 'antd';
import { CarOutlined, EnvironmentOutlined, ClockCircleOutlined, PhoneOutlined, UserOutlined, StarOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { fetchRideOrdersSuccess, updateRideOrderStatus, cancelRideOrder } from '../../store/slices/rideSlice';
import dayjs from 'dayjs';

const { Step } = Steps;
const { TabPane } = Tabs;
const { TextArea } = Input;

const RideOrderPage: React.FC = () => {
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const dispatch = useDispatch();
  const { orders, loading } = useSelector((state: RootState) => state.ride);

  // シミュレート打车注文数据
  const mockRideOrders = [
    {
      id: '1',
      serviceType: 'carpool' as const,
      passengerId: 'user1',
      driverId: 'driver1',
      pickupLocation: {
        city: '北京',
        address: '北京南站',
        coordinates: [116.4074, 39.9042] as [number, number],
      },
      dropoffLocation: {
        city: '天津',
        address: '天津站',
        coordinates: [117.2008, 39.0842] as [number, number],
      },
      scheduledTime: '2024-02-01 14:30:00',
      passengerCount: 1,
      totalAmount: 45,
      status: 'completed' as const,
      paymentStatus: 'paid' as const,
      driverInfo: {
        name: '张师傅',
        phone: '138****5678',
        vehiclePlate: '京A88888',
        rating: 4.8,
      },
      createdAt: '2024-01-28 10:00:00',
      updatedAt: '2024-02-01 16:30:00',
    },
    {
      id: '2',
      serviceType: 'charter' as const,
      passengerId: 'user1',
      driverId: 'driver2',
      pickupLocation: {
        city: '北京',
        address: '北京西站',
        coordinates: [116.3216, 39.8963] as [number, number],
      },
      dropoffLocation: {
        city: '石家庄',
        address: '石家庄站',
        coordinates: [114.5149, 38.0428] as [number, number],
      },
      scheduledTime: '2024-02-05 09:00:00',
      passengerCount: 3,
      vehicleType: 'business7' as const,
      priceMultiplier: 1.2,
      totalAmount: 456,
      status: 'accepted' as const,
      paymentStatus: 'paid' as const,
      driverInfo: {
        name: '李师傅',
        phone: '139****1234',
        vehiclePlate: '京B99999',
        rating: 4.9,
      },
      note: '必要儿童安全座椅',
      createdAt: '2024-02-01 15:30:00',
      updatedAt: '2024-02-01 15:45:00',
    },
    {
      id: '3',
      serviceType: 'carpool' as const,
      passengerId: 'user1',
      pickupLocation: {
        city: '北京',
        address: '三元桥',
        coordinates: [116.4551, 39.9645] as [number, number],
      },
      dropoffLocation: {
        city: '天津',
        address: '滨海国际机场',
        coordinates: [117.3462, 39.1244] as [number, number],
      },
      scheduledTime: '2024-02-10 16:00:00',
      passengerCount: 2,
      totalAmount: 90,
      status: 'pending' as const,
      paymentStatus: 'paid' as const,
      createdAt: '2024-02-02 11:20:00',
      updatedAt: '2024-02-02 11:20:00',
    },
  ];

  useEffect(() => {
    // シミュレート获取打车注文数据
    dispatch(fetchRideOrdersSuccess(mockRideOrders));
  }, [dispatch]);

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'orange',
      accepted: 'blue',
      picked_up: 'green',
      completed: 'purple',
      cancelled: 'red',
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      pending: '等待接单',
      accepted: 'ドライバー已接单',
      picked_up: '行程中',
      completed: '已完成',
      cancelled: '已キャンセル',
    };
    return textMap[status] || status;
  };

  const getServiceTypeText = (type: string) => {
    return type === 'carpool' ? '相乗り (拼车)' : '包车';
  };

  const getServiceTypeColor = (type: string) => {
    return type === 'carpool' ? 'blue' : 'orange';
  };

  const getCurrentStep = (status: string) => {
    const stepMap: Record<string, number> = {
      pending: 0,
      accepted: 1,
      picked_up: 2,
      completed: 3,
      cancelled: -1,
    };
    return stepMap[status] || 0;
  };

  const handleViewDetail = (order: any) => {
    setSelectedOrder(order);
    setDetailModalVisible(true);
  };

  const handleCancelOrder = (orderId: string) => {
    Modal.confirm({
      title: '確認キャンセル注文',
      content: 'キャンセル注文可能产生キャンセル费用，确定要キャンセル吗？',
      onOk: () => {
        dispatch(cancelRideOrder(orderId));
        message.success('注文已キャンセル');
      },
    });
  };

  const handleRateOrder = (order: any) => {
    setSelectedOrder(order);
    setRatingModalVisible(true);
  };

  const handleSubmitRating = () => {
    message.success('评价提交成功！');
    setRatingModalVisible(false);
    setRating(5);
    setReview('');
  };

  const handleCallDriver = (phone: string) => {
    message.info(`正在拨打ドライバー電話：${phone}`);
  };

  const filterOrdersByStatus = (status?: string) => {
    if (!status) return orders;
    return orders.filter(order => order.status === status);
  };

  const renderOrderList = (orderList: any[]) => {
    if (orderList.length === 0) {
      return <Empty description="暂无注文" />;
    }

    return (
      <List
        dataSource={orderList}
        renderItem={(order) => (
          <List.Item>
            <Card
              style={{ width: '100%' }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ marginRight: 8 }}>注文号：{order.id}</span>
                    <Tag color={getServiceTypeColor(order.serviceType)}>
                      {getServiceTypeText(order.serviceType)}
                    </Tag>
                  </div>
                  <Tag color={getStatusColor(order.status)}>
                    {getStatusText(order.status)}
                  </Tag>
                </div>
              }
              actions={[
                <Button type="link" onClick={() => handleViewDetail(order)}>
                  確認詳細
                </Button>,
                ...(order.driverInfo && order.status !== 'completed' && order.status !== 'cancelled' ? [
                  <Button type="link" icon={<PhoneOutlined />} onClick={() => handleCallDriver(order.driverInfo.phone)}>
                    联系ドライバー
                  </Button>
                ] : []),
                ...(order.status === 'completed' ? [
                  <Button type="link" icon={<StarOutlined />} onClick={() => handleRateOrder(order)}>
                    评价ドライバー
                  </Button>
                ] : []),
                ...(order.status === 'pending' || order.status === 'accepted' ? [
                  <Button danger type="link" onClick={() => handleCancelOrder(order.id)}>
                    キャンセル注文
                  </Button>
                ] : []),
              ]}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <EnvironmentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                    <span>{order.pickupLocation.address}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    <span>{order.dropoffLocation.address}</span>
                  </div>
                  <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {dayjs(order.scheduledTime).format('YYYY-MM-DD HH:mm')}
                  </p>
                  <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                    <UserOutlined style={{ marginRight: 4 }} />
                    {order.passengerCount}人
                    {order.vehicleType && ` · ${order.vehicleType === 'business5' ? '商务5座' : '商务7座'}`}
                  </p>
                  {order.driverInfo && (
                    <p style={{ margin: '4px 0', color: '#666', display: 'flex', alignItems: 'center' }}>
                      <CarOutlined style={{ marginRight: 4 }} />
                      ドライバー：{order.driverInfo.name} · {order.driverInfo.vehiclePlate} · ⭐{order.driverInfo.rating}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                    ¥{order.totalAmount}
                  </div>
                  {order.priceMultiplier && order.priceMultiplier > 1 && (
                    <div style={{ fontSize: 12, color: '#ff7875' }}>
                      {order.priceMultiplier}x 倍价
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    下单時間：{dayjs(order.createdAt).format('MM-DD HH:mm')}
                  </div>
                </div>
              </div>
            </Card>
          </List.Item>
        )}
      />
    );
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <div className="container">
        <Card title="我的用车注文">
          <Tabs defaultActiveKey="all">
            <TabPane tab="全部注文" key="all">
              {renderOrderList(orders)}
            </TabPane>
            <TabPane tab="等待接单" key="pending">
              {renderOrderList(filterOrdersByStatus('pending'))}
            </TabPane>
            <TabPane tab="进行中" key="active">
              {renderOrderList([
                ...filterOrdersByStatus('accepted'),
                ...filterOrdersByStatus('picked_up')
              ])}
            </TabPane>
            <TabPane tab="已完成" key="completed">
              {renderOrderList(filterOrdersByStatus('completed'))}
            </TabPane>
            <TabPane tab="已キャンセル" key="cancelled">
              {renderOrderList(filterOrdersByStatus('cancelled'))}
            </TabPane>
          </Tabs>
        </Card>

        {/* 注文詳細模态框 */}
        <Modal
          title="注文詳細"
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          width={700}
          footer={null}
        >
          {selectedOrder && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <Tag color={getServiceTypeColor(selectedOrder.serviceType)}>
                  {getServiceTypeText(selectedOrder.serviceType)}
                </Tag>
                <Tag color={getStatusColor(selectedOrder.status)}>
                  {getStatusText(selectedOrder.status)}
                </Tag>
              </div>

              <Descriptions bordered size="small" style={{ marginBottom: 20 }}>
                <Descriptions.Item label="注文号" span={2}>{selectedOrder.id}</Descriptions.Item>
                <Descriptions.Item label="服务类型">
                  {getServiceTypeText(selectedOrder.serviceType)}
                </Descriptions.Item>
                <Descriptions.Item label="出发地" span={3}>
                  {selectedOrder.pickupLocation.city} - {selectedOrder.pickupLocation.address}
                </Descriptions.Item>
                <Descriptions.Item label="目的地" span={3}>
                  {selectedOrder.dropoffLocation.city} - {selectedOrder.dropoffLocation.address}
                </Descriptions.Item>
                <Descriptions.Item label="出发時間">{selectedOrder.scheduledTime}</Descriptions.Item>
                <Descriptions.Item label="乘客人数">{selectedOrder.passengerCount}人</Descriptions.Item>
                <Descriptions.Item label="支付ステータス">
                  <Tag color={selectedOrder.paymentStatus === 'paid' ? 'green' : 'orange'}>
                    {selectedOrder.paymentStatus === 'paid' ? '已支付' : '未支付'}
                  </Tag>
                </Descriptions.Item>
                {selectedOrder.vehicleType && (
                  <Descriptions.Item label="车型">
                    {selectedOrder.vehicleType === 'business5' ? '商务5座' : '商务7座'}
                  </Descriptions.Item>
                )}
                {selectedOrder.priceMultiplier && selectedOrder.priceMultiplier > 1 && (
                  <Descriptions.Item label="价格倍数">{selectedOrder.priceMultiplier}x</Descriptions.Item>
                )}
                <Descriptions.Item label="总费用">¥{selectedOrder.totalAmount}</Descriptions.Item>
                <Descriptions.Item label="下单時間">{selectedOrder.createdAt}</Descriptions.Item>
                {selectedOrder.note && (
                  <Descriptions.Item label="备注" span={3}>{selectedOrder.note}</Descriptions.Item>
                )}
              </Descriptions>

              {selectedOrder.driverInfo && (
                <Card title="ドライバー情報" size="small" style={{ marginBottom: 20 }}>
                  <Descriptions size="small">
                    <Descriptions.Item label="氏名">{selectedOrder.driverInfo.name}</Descriptions.Item>
                    <Descriptions.Item label="電話">{selectedOrder.driverInfo.phone}</Descriptions.Item>
                    <Descriptions.Item label="车牌号">{selectedOrder.driverInfo.vehiclePlate}</Descriptions.Item>
                    <Descriptions.Item label="评分">
                      <Rate disabled value={selectedOrder.driverInfo.rating} style={{ fontSize: 14 }} />
                      <span style={{ marginLeft: 8 }}>{selectedOrder.driverInfo.rating}</span>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}

              {selectedOrder.status !== 'cancelled' && (
                <div style={{ marginTop: 20 }}>
                  <h4>注文进度</h4>
                  <Steps current={getCurrentStep(selectedOrder.status)} size="small">
                    <Step title="等待接单" description="正在为您匹配ドライバー" />
                    <Step title="ドライバー接单" description="ドライバー已確認，准备出发" />
                    <Step title="行程开始" description="ドライバー已到达，开始行程" />
                    <Step title="行程完成" description="已到达目的地，行程结束" />
                  </Steps>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* 评价ドライバー模态框 */}
        <Modal
          title="评价ドライバー"
          open={ratingModalVisible}
          onCancel={() => setRatingModalVisible(false)}
          onOk={handleSubmitRating}
          width={500}
        >
          {selectedOrder && selectedOrder.driverInfo && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 20 }}>
                <CarOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                <h3 style={{ marginTop: 16 }}>{selectedOrder.driverInfo.name}</h3>
                <p style={{ color: '#666' }}>{selectedOrder.driverInfo.vehiclePlate}</p>
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <p style={{ marginBottom: 8 }}>お对本次服务进行评价：</p>
                <Rate value={rating} onChange={setRating} style={{ fontSize: 32 }} />
              </div>

              <TextArea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="お输入您的评价（选填）"
                rows={4}
                maxLength={200}
              />
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RideOrderPage;